import { HTML_LANG, i18next, type UiLocale } from '@lb/i18n'
import { PLATFORM_TIMEZONE } from '@lb/domain'

/**
 * Dates and times, in the member's UI language and the platform's timezone.
 *
 * ── Two different things that both look like "locale" ─────────────────────
 * The **UI language** decides the words — "12 August" versus "12 août". The
 * **timezone** decides the day, and it is `America/Montreal` for everybody
 * regardless of language, because the platform's day boundaries are Québec's
 * (§4.10). A parent logging at 21:00 is already tomorrow in UTC; formatting in
 * the device's zone would show them a date the server disagrees with.
 *
 * This module exists because every screen had `new Intl.DateTimeFormat('fr-CA',
 * …)` written into it. That is correct output for a French member and wrong for
 * an English one, and it was invisible while there was only one language.
 *
 * `i18next.language` rather than a hook parameter: these are called from render
 * paths that already re-render on a language change (the whole tree does,
 * because `useTranslation` subscribes), and threading a locale through every
 * call site is the ceremony this replaces.
 */

function bcp47(): string {
  const language = i18next.language as UiLocale
  return HTML_LANG[language] ?? HTML_LANG.fr
}

/**
 * « le 1er octobre », not « le 1 octobre » (14 Oct 2026).
 *
 * French ordinals the first of the month and nothing else — `Intl` does not,
 * in any locale or option combination, so every date landing on the 1st has
 * read wrong in French since this module was written. Found on the billing
 * card, where a member who cancelled on 1 October was told « Vous avez annulé
 * le 1 octobre ».
 *
 * A targeted rewrite rather than a hand-built formatter: `Intl` keeps owning
 * the month names, the order and the English output, and this touches the one
 * token it gets wrong. The boundary `(?<!\d)1(?!\d)` is so « 11 », « 21 » and
 * « 2031 » are left alone.
 */
function frenchFirst(text: string, locale: string, date: Date): string {
  if (!locale.startsWith('fr')) return text
  /* The day in MONTREAL, not on the device: `getDate()` would answer the
   * viewer's own zone and disagree with the string we are rewriting. */
  const day = new Intl.DateTimeFormat('en-CA', {
    day: 'numeric',
    timeZone: PLATFORM_TIMEZONE,
  }).format(date)
  if (day !== '1') return text
  return text.replace(/(?<!\d)1(?!\d)/, '1er')
}

/** "12 août 2026" / "12 August 2026". */
export function formatDate(iso: string): string {
  const locale = bcp47()
  const date = new Date(iso)
  return frenchFirst(
    new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: PLATFORM_TIMEZONE }).format(date),
    locale,
    date,
  )
}

/** "12 août" / "12 Aug" — the compact form for a list row. */
export function formatDateShort(iso: string): string {
  const locale = bcp47()
  const date = new Date(iso)
  return frenchFirst(
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: PLATFORM_TIMEZONE }).format(date),
    locale,
    date,
  )
}

/** "19 h 00" / "7:00 PM" — the 12/24-hour split is the locale's, not ours. */
export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat(bcp47(), {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: PLATFORM_TIMEZONE,
  }).format(new Date(iso))
}

/**
 * "12 août 2026, 14 h 30" — a date AND its time, for a deadline short enough
 * that the date alone would mislead.
 *
 * An email-change link lives about an hour. Rendering that as "6 August" reads
 * as "you have until midnight", which is wrong by most of a day in the
 * direction that costs somebody their link.
 */
export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat(bcp47(), {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: PLATFORM_TIMEZONE,
  }).format(new Date(iso))
}

/** "août 2026" — the month heading a list groups under. */
export function formatMonth(iso: string): string {
  return new Intl.DateTimeFormat(bcp47(), {
    month: 'long',
    year: 'numeric',
    timeZone: PLATFORM_TIMEZONE,
  }).format(new Date(iso))
}

export function formatDayOfMonth(iso: string): string {
  return new Intl.DateTimeFormat(bcp47(), {
    day: 'numeric',
    timeZone: PLATFORM_TIMEZONE,
  }).format(new Date(iso))
}

export function formatMonthShort(iso: string): string {
  return new Intl.DateTimeFormat(bcp47(), {
    month: 'short',
    timeZone: PLATFORM_TIMEZONE,
  }).format(new Date(iso))
}

/**
 * A calendar day (`YYYY-MM-DD`) as a **local** `Date` at midnight.
 *
 * `new Date('2026-08-11')` is UTC midnight, which is 20:00 on the 10th in
 * Montréal — so every naive read of a calendar day is a day early for the
 * entire Québec user base. This is the one place that parse happens.
 *
 * Returns `null` for anything that is not three numbers, so a caller has to
 * decide what an unparseable day means rather than getting `Invalid Date`.
 */
export function parseCalendarDay(day: string): Date | null {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

/**
 * An **instant** → the calendar day it falls on in Montréal.
 *
 * The platform's day boundaries are Québec's (§4.10), and the API mixes the
 * two shapes freely: `joinedOn` is a calendar day, `earnedOn` is a UTC
 * timestamp. Comparing them without this converts in UTC, which moves every
 * evening in Montréal into the following day.
 */
export function calendarDayInMontreal(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: PLATFORM_TIMEZONE,
  }).format(new Date(iso))
}

/**
 * Whole days from one calendar day to another. Negative if `to` is earlier.
 *
 * Both sides parsed as local parts, so the arithmetic never crosses a zone —
 * and rounded rather than floored, because a DST boundary between the two
 * makes one of the intervals 23 or 25 hours long and flooring that loses a
 * day twice a year.
 */
export function daysBetweenCalendarDays(from: string, to: string): number | null {
  const a = parseCalendarDay(from)
  const b = parseCalendarDay(to)
  if (!a || !b) return null
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/**
 * A **calendar day** (`YYYY-MM-DD`), not a timestamp.
 *
 * Parsed as local parts rather than through `new Date('2026-08-12')`, which
 * ECMAScript parses as **UTC midnight** — so a member west of Greenwich sees
 * the day before. That is the bug this function exists to not have.
 */
export function formatCalendarDay(day: string, style: 'long' | 'medium' = 'long'): string {
  const date = parseCalendarDay(day)
  if (!date) return day
  return frenchFirstOfMonth(new Intl.DateTimeFormat(bcp47(), { dateStyle: style }).format(date))
}

/**
 * French writes the first of a month as an ordinal — « le 1er avril », never
 * « le 1 avril » — and `Intl` prints the cardinal. Only the leading day is
 * touched, and only in French.
 */
export function frenchFirstOfMonth(formatted: string): string {
  if (!bcp47().toLowerCase().startsWith('fr')) return formatted
  return formatted.replace(/^1(?=\s)/, '1er')
}

/**
 * `September 4` / `4 septembre` — a calendar day with the year left off.
 *
 * `formatCalendarDay` has only `dateStyle`, and both of its styles print the
 * year. The design's cohort strip writes "next milestone: Live supervision II,
 * **September 4**" — inside a sentence that has already said which month of a
 * nine-month programme you are in, the year is four characters that answer a
 * question nobody asked. Same local-parts rule as its neighbours.
 */
export function formatMonthDay(day: string): string {
  const date = parseCalendarDay(day)
  if (!date) return day
  return frenchFirstOfMonth(new Intl.DateTimeFormat(bcp47(), { month: 'long', day: 'numeric' }).format(date))
}

/**
 * "août 2026" from a **calendar day**, not a timestamp.
 *
 * `formatMonth` exists above and takes an instant. Passing it a `YYYY-MM-DD`
 * gives the WRONG MONTH for the first of any month west of Greenwich —
 * `2026-08-01` is UTC midnight, which is 31 July in Montreal, so a month
 * heading built from the first of the month reads as the previous one. Same
 * local-parts rule as `formatCalendarDay`.
 */
export function formatCalendarMonth(day: string): string {
  const date = parseCalendarDay(day)
  if (!date) return day
  return new Intl.DateTimeFormat(bcp47(), { month: 'long', year: 'numeric' }).format(date)
}

/** « août » — a month without its year, for a week that began in the month before. */
export function formatMonthName(day: string): string {
  const date = parseCalendarDay(day)
  if (!date) return day
  return new Intl.DateTimeFormat(bcp47(), { month: 'long' }).format(date)
}

/** « mercredi 7 octobre » — a day of the week running, so no year. */
export function formatWeekdayAndDay(day: string): string {
  const date = parseCalendarDay(day)
  if (!date) return day
  const formatted = new Intl.DateTimeFormat(bcp47(), { weekday: 'long', day: 'numeric', month: 'long' }).format(date)
  // « jeudi 1er octobre » — the ordinal sits after the weekday here.
  return bcp47().toLowerCase().startsWith('fr') ? formatted.replace(/ 1(?= )/, ' 1er') : formatted
}

/** The weekday initial for the challenge grid. Same local-parts rule. */
export function formatWeekday(day: string): string {
  const date = parseCalendarDay(day)
  if (!date) return day
  return new Intl.DateTimeFormat(bcp47(), { weekday: 'short' }).format(date)
}

/** The hour, as a number, for the time-aware greeting. Platform timezone. */
export function hourInMontreal(): number {
  return Number(
    new Intl.DateTimeFormat('en-CA', {
      hour: 'numeric',
      hour12: false,
      timeZone: PLATFORM_TIMEZONE,
    }).format(new Date()),
  )
}

/**
 * Today, in the platform's timezone, as the hero's eyebrow prints it.
 *
 * The timezone is the platform's and not the device's for the same reason
 * `hourInMontreal` is: a member in Vancouver opening the app at 22:00 local is
 * on the same La Boussole day as everyone else, and a date that disagrees with
 * the greeting above it looks like a bug.
 */
export function todayInMontreal(locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: PLATFORM_TIMEZONE,
  }).format(new Date())
}

/**
 * Today as `YYYY-MM-DD`, in the platform's timezone — for comparing against
 * the API's calendar days.
 *
 * `occursOn` and `endsOn` are days with no time and no zone. Turning one into
 * a `Date` makes it midnight UTC, which is the previous evening in Montréal —
 * so a block ending `2026-08-28` would read as over from the 27th for every
 * member. Comparing ISO day strings lexicographically is exact, ordering-safe
 * for any two dates in the same format, and has no zone left to get wrong.
 *
 * `en-CA` because its short date format IS `YYYY-MM-DD`; the locale here is a
 * formatting instruction, not the member's language.
 */
export function todayISOInMontreal(): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: PLATFORM_TIMEZONE,
  }).format(new Date())
}

/**
 * "8 min", "2 h", "hier" — the notification feed's timestamps.
 *
 * `Intl.RelativeTimeFormat` rather than a hand-rolled ladder, because the
 * pluralisation and the word order are the locale's problem and it already
 * knows them. French says "il y a 2 heures" and English says "2 hours ago";
 * a template with a translated suffix gets one of those wrong.
 *
 * `numeric: 'auto'` is what turns -1 day into "hier" instead of "il y a 1 jour".
 */
export function formatRelativeTime(iso: string, locale: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''

  const seconds = Math.round((then - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ]

  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit)
  }
  // Under a minute reads better as a word than as "in 0 seconds".
  return rtf.format(0, 'minute')
}

/**
 * `4,2 Mo` / `4.2 MB` — a file size in the member's language.
 *
 * `Intl.NumberFormat`'s unit style rather than a `Mo`/`MB` pair in the bundle:
 * it gets the decimal separator right (`4,2` in French, `4.2` in English) and
 * the abbreviation is a localisation, not copy — French Canada writes `ko`
 * where English writes `kB`, and neither is a string a translator should have
 * to know.
 *
 * KB below one megabyte, because a case study that came out at `0 Mo` is the
 * one number a member most needs to read as wrong. One decimal under 10 MB and
 * none above, so `48 Mo` does not pretend to a precision the choice of 1024
 * does not have.
 */
export function formatFileSize(bytes: number): string {
  const kb = bytes < 1024 * 1024
  const value = kb ? bytes / 1024 : bytes / (1024 * 1024)

  return new Intl.NumberFormat(bcp47(), {
    style: 'unit',
    unit: kb ? 'kilobyte' : 'megabyte',
    unitDisplay: 'short',
    maximumFractionDigits: kb || value >= 10 ? 0 : 1,
  }).format(value)
}

/**
 * « Mensuel ou Annuel » / "Monthly or Annual".
 *
 * A disjunction, because it lists plans an event is reserved for and any ONE
 * of them opens it — « et » would say a member needs all of them. `Intl`
 * rather than a hand-joined string so French gets « ou » and three plans get
 * the right commas in each language.
 */
export function formatOrList(items: string[]): string {
  return new Intl.ListFormat(bcp47(), { type: 'disjunction' }).format(items)
}

/**
 * A price in the reader's own convention: « 19 $ » / "$19".
 *
 * Whole amounts drop the ",00" — the plan cards print a price, not a receipt —
 * and anything with cents keeps both digits, so « 16,33 $ » never reads « 16,3 $ ».
 * `fr-CA`/`en-CA`, not the bare language: `en` with CAD prints "CA$19".
 */
export function formatPrice(cents: number, currency: string): string {
  const whole = cents % 100 === 0
  return new Intl.NumberFormat(bcp47(), {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

/** « 14 % » / "14%" — a saving, rounded to the whole percent. */
export function formatPercent(fraction: number): string {
  return new Intl.NumberFormat(bcp47(), { style: 'percent', maximumFractionDigits: 0 }).format(fraction)
}
